import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createAppRouter } from './app/router.js';
import { AuthProvider } from './lib/auth.js';
import { ToastProvider } from './lib/toast.js';
import { useApplyBranding } from './features/branding/apply-branding.js';
import './styles/index.css';

// Dev-only visual-diff harness (design/orion-crm-design/CLAUDE_CODE_HANDOFF.md
// § Phase 0): `?__design=<Screen>` swaps every API call for fixture data and
// lets the REAL router/app render the REAL screen at its REAL URL — no
// separate preview route or component-wrapping needed, so route-bound hooks
// (`useParams`, `useSearch`, ...) work exactly as they do for a real
// visitor. `import.meta.env.DEV` makes the whole branch, and everything it
// imports, dead-code-eliminated out of a production build.
let designScreen: string | null = null;
if (import.meta.env.DEV) {
  designScreen = new URLSearchParams(window.location.search).get('__design');
  if (designScreen) {
    const { SCREEN_REGISTRY, allFixturesFor } = await import('./design-preview/registry.js');
    const { installFixtureFetch } = await import('./design-fixtures/fixture-fetch.js');
    const entry = SCREEN_REGISTRY[designScreen];
    if (entry) installFixtureFetch(allFixturesFor(entry));
    else console.error(`No design-preview fixtures registered for "${designScreen}".`);
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: designScreen ? false : 1, refetchOnWindowFocus: false } },
});
const router = createAppRouter(queryClient);

function App() {
  useApplyBranding();
  return (
    <ToastProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ToastProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
